import { createApp, reactive } from "petite-vue"

type QueryParams = {
    [key: string]: string | string[]
}

interface ArchiveData {
    title: string
    offers: string[]
    totalCount: number
    foundPosts: number
    page: number
    nextPageUrl: string | null
    pager: string
    query: QueryParams
    apiUrl: string
    url: string
    queryOrder: string[]
    filterParams: {
        [key: string]: {
            key: string
            label: string
            type: string
            options?: { [key: string]: string }
            unit?: string
        }
    }
    mapPoints?: any[]
}

declare global {
    const archiveData: ArchiveData
}

type StateFilter = "k-dispozici" | "archiv"

const FIXED_PER_PAGE = 12

interface FilterParamTag {
    key: string
    value: string
    label: string
}

interface ArchiveStore extends ArchiveData {
    appendPosts: boolean
    scrollIntoView: boolean
    isLoading: boolean
    order: string
    search: string
    filteredCount: number
    queryOrder: string[]
    selectedParams: QueryParams

    // Advanced options logic
    showAdvanced: boolean
    toggleAdvanced(): void

    // Dropdown logic
    activeDropdown: string | null
    toggleDropdown(key: string): void
    closeDropdown(): void

    // Address logic
    addressSuggestions: any[]
    addressDebounceTimer: any
    handleAddressInput(event: Event): void
    selectAddress(suggestion: any): void

    // Getters
    readonly selectedParamsTags: FilterParamTag[]
    readonly hasActiveFilters: boolean

    // Methods
    setQuery(params: any, resetPager?: boolean, resetQuery?: boolean): void
    removeParameter(key: string, value: string): void
    changeState(state: StateFilter): void
    updatePillBackground(): void
    changeOrder(): void
    changePage(page: number): void
    loadNextPage(): void
    openParametersFilter(e: Event): void
    closeParametersFilter(e: Event): void
    handleFilterClosing(): void
    searchOffers(): void
    clearSearch(): void
    filterOffers(): void
    clearFilter(): void
    prepareQueryString(queryParams?: any, type?: string): string
    getArchive(queryParams?: any): Promise<void>
    getSummary(): Promise<void>

    // Helpers
    ui: {
        locality: string
        saleType: string
        estate: string
    }
    clearKey(key: string): void
    clearPrice(key: string): void
    readonly hasLocality: boolean
    clearLocality(): void
    handleLocalityCheckboxChange(): void
    handleDialogLocalityChange(): void
    readonly dialogActiveCount: number
    toggleParam(key: string, value: string): void
}

function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // remove diacritics
        .replace(/[^a-z0-9\s-_]/g, "") // strip commas, etc.
        .trim()
        .replace(/[\s-_]+/g, "-") // single hyphens
        .replace(/^-+|-+$/g, ""); // trim hyphens
}

function getTargetPathname(currentPath: string, archiveUrl: string, queryParams: any): string {
    try {
        const archivePath = new URL(archiveUrl, window.location.origin).pathname;

        const normalize = (p: string) => p.endsWith('/') ? p : p + '/';
        const normCurrent = normalize(currentPath);
        const normArchive = normalize(archivePath);

        if (normCurrent === normArchive) {
            return currentPath;
        }

        let taxKey = '';
        let taxSlug = '';

        const parts = normCurrent.split('/').filter(Boolean);
        const taxIndex = parts.findIndex(p => p === 'druh-nemovitosti' || p === 'typ-nabidky' || p === 'okres');

        if (taxIndex !== -1 && parts[taxIndex + 1]) {
            const taxType = parts[taxIndex];
            taxSlug = parts[taxIndex + 1];

            if (taxType === 'druh-nemovitosti') {
                taxKey = 'nemovitost';
            } else if (taxType === 'typ-nabidky') {
                taxKey = 'typ_prodeje';
            } else if (taxType === 'okres') {
                taxKey = 'lokalita';
            }
        }

        if (taxKey && taxSlug) {
            const paramValue = queryParams[taxKey];
            if (paramValue) {
                const values = String(paramValue).split(',').map(v => v.trim());
                // Stay on the taxonomy page ONLY if this taxonomy term is the sole selected value.
                // If the user picks additional types (e.g. domy + komercni-nemovitosti),
                // redirect to the main archive so the URL stays clean and accurate.
                if (values.length === 1 && values[0] === taxSlug) {
                    return currentPath;
                }
            }

            return archivePath;
        }
    } catch (e) {
        console.error("Error resolving target URL pathname:", e);
    }

    return currentPath;
}

function getFormattedAddress(suggestion: any): { label: string, type: 'city' | 'street' | 'other' } {
    const addr = suggestion.address || {};
    
    // Determine city/town/village
    const city = addr.city || addr.town || addr.village || addr.municipality || "";
    // Determine street
    let street = addr.road || addr.pedestrian || addr.square || addr.footway || addr.cycleway || addr.path || "";
    
    // Suburb
    const suburb = addr.suburb || addr.neighbourhood || addr.quarter || addr.city_district || "";
    
    // Fallback: If street is empty, check if suggestion has a name (e.g. peak, forest, locality, etc.)
    const suggestionName = suggestion.name || "";
    if (!street && suggestionName) {
        const lowerName = suggestionName.toLowerCase();
        const isAdm = lowerName === (city as string).toLowerCase() ||
                      lowerName === (suburb as string).toLowerCase() ||
                      lowerName === (addr.county as string || "").toLowerCase() ||
                      lowerName === (addr.state as string || "").toLowerCase() ||
                      lowerName === (addr.country as string || "").toLowerCase();
        if (!isAdm) {
            street = suggestionName;
        }
    }
    
    let label = "";
    let type: 'city' | 'street' | 'other' = 'other';
    
    if (street) {
        label = city ? `${street}, ${city}` : street;
        type = 'street';
    } else if (suburb && city && suburb !== city) {
        label = `${suburb}, ${city}`;
        type = 'street';
    } else if (city) {
        label = city;
        type = 'city';
    } else {
        label = suggestion.display_name || "";
    }
    
    return { label, type };
}

export default function initArchiveOffer(): void {
    const archiveGrid = document.querySelector("[data-offer-grid]")
    if (!archiveGrid || !archiveData) return

    const queryToSelectedParams = (query: QueryParams, filterParams: any = null) => {
        const currentFilterParams = filterParams || archiveData.filterParams

        return Object.values(currentFilterParams ?? {}).reduce<QueryParams>(
            (params, parameter) => {
                const newParams: any = {}

                if (parameter.type === "range") {
                    ;["od", "do"].forEach((range) => {
                        const key = `${parameter.key}_${range}`
                        newParams[key] = query[key] ?? ""
                    })
                } else {
                    const isSingle = parameter.type !== "check"
                    const queryValue = query[parameter.key] as string

                    newParams[parameter.key] = isSingle
                        ? queryValue ?? ""
                        : queryValue?.split(",") ?? []
                }

                return { ...params, ...newParams }
            },
            {
                stav: (query["stav"] ?? "k-dispozici") as any,
                adresa_text: query["adresa_text"] ?? "",
                lat: (query["lat"] ?? "") as string,
                lng: (query["lng"] ?? "") as string,
                radius: (query["radius"] ?? "") as string,
            }
        )
    }

    const archive: ArchiveStore = reactive({
        ...archiveData,

        appendPosts: false,
        scrollIntoView: false,
        isLoading: false,

        order: (archiveData.query["razeni"] ?? "nejnovejsi") as string,
        search: (archiveData.query["hledat"] ?? "") as string,
        filteredCount: archiveData.totalCount ?? 0,

        // pojistka: aby se "stav" vždy serializoval do URL
        // pojistka: aby se "stav" vždy serializoval do URL
        queryOrder: Array.from(new Set([...(archiveData.queryOrder ?? []), "stav", "adresa_text", "lat", "lng", "radius"])),

        // --- Advanced Options Logic ---
        showAdvanced: false,

        toggleAdvanced() {
            this.showAdvanced = !this.showAdvanced
        },

        // --- Dropdown Logic ---
        activeDropdown: null,

        toggleDropdown(key: string) {
            if (this.activeDropdown === key) {
                this.activeDropdown = null
            } else {
                this.activeDropdown = key
            }
        },

        closeDropdown() {
            this.activeDropdown = null
        },

        selectedParams: queryToSelectedParams(archiveData.query),

        get selectedParamsTags(): FilterParamTag[] {
            const tags: FilterParamTag[] = []

            const baseKeyOf = (key: string) => key.replace(/_(od|do)$/, "")
            const rangePartOf = (key: string) =>
                key.endsWith("_od") ? "od" : key.endsWith("_do") ? "do" : ""

            Object.entries(this.selectedParams).forEach(([key, rawValue]) => {
                const baseKey = baseKeyOf(key)
                const parameter = this.filterParams?.[baseKey]
                if (!parameter) return

                if (parameter.type === "range") {
                    const part = rangePartOf(key)
                    const num = String(rawValue ?? "").trim()
                    if (!part || num === "") return

                    const unit = (parameter.unit ?? "").replace("m2", "m<sup>2</sup>")
                    const formatted = Number(num).toLocaleString()
                    const label = `${part} ${formatted}${unit ? ` ${unit}` : ""}`

                    tags.push({ key, value: num, label })
                    return
                }

                const terms = Array.isArray(rawValue) ? rawValue : [rawValue]
                terms
                    .map((v) => String(v ?? "").trim())
                    .filter((v) => v !== "")
                    .forEach((term) => {
                        const label =
                            parameter.options && parameter.options[term]
                                ? parameter.options[term]
                                : term

                        tags.push({
                            key: baseKey,
                            value: term,
                            label,
                        })
                    })
            })

            return tags
        },

        get hasActiveFilters(): boolean {
            if (this.search) return true

            const ignoreKeys = ["stav", "lat", "lng", "radius"]
            return Object.entries(this.selectedParams).some(([key, val]) => {
                if (ignoreKeys.includes(key)) return false
                if (Array.isArray(val)) return val.length > 0
                return String(val ?? "").trim() !== ""
            })
        },

        setQuery(params: any = {}, resetPager = false, resetQuery = false) {
            const query = params
                ? {
                    ...(resetQuery ? {} : this.query),
                    ...params,
                    ...(resetPager ? { strana: 1 } : {}),
                    // ✅ natvrdo vždy 12
                    pocet: FIXED_PER_PAGE,
                }
                : { pocet: FIXED_PER_PAGE }

            // Always ensure sorting and search are present unless explicitly overridden
            if (!query.hasOwnProperty("razeni")) query.razeni = this.order
            if (!query.hasOwnProperty("hledat")) query.hledat = this.search

            this.getArchive(query)
        },

        removeParameter(key: string, value: string) {
            if (!this.selectedParams.hasOwnProperty(key)) return

            const current = (this.selectedParams as any)[key]

            if (Array.isArray(current)) {
                ; (this.selectedParams as any)[key] = current.filter((t: string) => t !== value)
            } else {
                ; (this.selectedParams as any)[key] = ""
            }

            this.setQuery(this.selectedParams, true, true)
        },

        changeState(state: StateFilter) {
            this.selectedParams["stav"] = state as any
            this.page = 1
            this.scrollIntoView = false
            this.setQuery({ stav: state }, true)

            // Update pill background size/position after state change
            setTimeout(() => this.updatePillBackground(), 0)
        },

        updatePillBackground() {
            // Pill position and width are now handled entirely by CSS.
            // The pill is always 50% wide and translateX(0%/100%) is
            // driven by the data-state attribute — no JS calculation needed.
        },

        changeOrder() {
            this.setQuery({ razeni: this.order }, true)
        },

        changePage(page: number) {
            this.scrollIntoView = true
            this.setQuery({ strana: page })
        },

        loadNextPage() {
            this.appendPosts = true
            this.setQuery({ strana: this.page + 1 })
        },

        openParametersFilter(e: Event) {
            const target = e.target as HTMLElement
            const dialog = target?.closest("aside")?.querySelector("dialog")
            if (!dialog) return

            dialog.showModal()

            // Close when clicking backdrop (click outside)
            dialog.onclick = (event) => {
                if (event.target === dialog) {
                    this.closeParametersFilter(event)
                }
            }
        },

        closeParametersFilter(e: Event) {
            const target = e.target as HTMLElement
            target.closest("dialog")?.close()
        },

        handleFilterClosing() {
            if (this.isLoading) return

            this.search = archive.query["hledat"] ?? ""
            this.order = archive.query["razeni"] ?? "nejnovejsi"
            this.selectedParams = queryToSelectedParams(this.query as any, this.filterParams)
            this.filteredCount = this.totalCount
        },

        searchOffers() {
            this.setQuery({ hledat: this.search }, true, true)
        },

        clearSearch() {
            this.search = ""
            this.setQuery({ hledat: "" }, true, true)
        },

        filterOffers() {
            this.setQuery(this.selectedParams, true, true)
        },

        clearFilter() {
            this.search = ""
            this.selectedParams = queryToSelectedParams({ stav: this.selectedParams["stav"] } as any, this.filterParams)
            this.setQuery(this.selectedParams, true, true)
        },

        prepareQueryString(queryParams: any = {}, type: string = "") {
            const query = new URLSearchParams()

            // ✅ pojistka: i kdyby to přišlo bez pocet, v URL bude vždy 12
            queryParams = { ...queryParams, pocet: FIXED_PER_PAGE }

            this.queryOrder.forEach((param: string) => {
                let value: string | string[] = queryParams[param] ?? ""
                if (!queryParams.hasOwnProperty(param)) return

                if (param === 'adresa_text' && typeof value === 'string' && value.trim() !== '') {
                    value = slugify(value);
                }

                query.append(
                    param,
                    Array.isArray(value) ? value.sort().join(",") : String(value)
                )
            })

            query.get("strana") === "1" && query.delete("strana")
            query.get("razeni") === "nejnovejsi" && query.delete("razeni")

                ;[...query.entries()].forEach(([key, value]) => {
                    ; (value === "" || value === "default") && query.delete(key)
                })

            type?.length && query.set("return", type)
            query.set("_t", Date.now().toString())

            return [...query].length ? `?${decodeURIComponent(query.toString())}` : ""
        },

        async getArchive(queryParams: any = {}) {
            this.isLoading = true

            const queryString = this.prepareQueryString(queryParams)
            const targetUrl = `${location.pathname}${queryString}`

            await fetch(`${this.apiUrl}${queryString}`, { method: "GET" })
                .then((response) => response.json())
                .then((data) => {
                    this.offers = [...(this.appendPosts ? this.offers : []), ...data.offers]
                    this.totalCount = this.filteredCount = data.totalCount
                    this.foundPosts = data.foundPosts
                    this.page = data.page
                    this.pager = data.pager
                    this.nextPageUrl = data.nextPageUrl
                    this.filterParams = data.filterParams
                    this.query = data.query
                    this.search = data.query["hledat"] ?? ""
                    this.order = data.query["razeni"] ?? "nejnovejsi"
                    
                    const oldAddress = this.selectedParams['adresa_text']
                    this.selectedParams = queryToSelectedParams(data.query, data.filterParams)
                    if (typeof oldAddress === 'string' && oldAddress.trim() !== '') {
                        if (slugify(oldAddress) === slugify(String(data.query["adresa_text"] || ''))) {
                            this.selectedParams['adresa_text'] = oldAddress
                        }
                    }

                    const h1 = document.querySelector("h1")
                    if (h1) {
                        h1.innerText = data.title
                    }

                    // Dynamically update browser tab title
                    if (data.title) {
                        const siteName = document.title.includes(' | ')
                            ? document.title.substring(document.title.indexOf(' | ') + 3)
                            : 'Děláme reality srdcem';
                        document.title = `${data.title} | ${siteName}`;
                    }

                    this.scrollIntoView &&
                        !this.appendPosts &&
                        (archiveGrid as HTMLElement)?.scrollIntoView()

                    // Trigger map update
                    if (data.mapPoints) {
                        const mapEl = document.querySelector('.Map[data-map="properties"]')
                        if (mapEl) {
                            mapEl.dispatchEvent(new CustomEvent('map:update-points', {
                                detail: { points: data.mapPoints }
                            }))
                        }
                    }

                    if (!queryParams.hasOwnProperty("noHistory")) {
                        const historyQuery = new URLSearchParams(queryString)
                        historyQuery.delete("_t")
                        if (historyQuery.get("pocet") === "12") historyQuery.delete("pocet")

                        // Dynamically update URL pathname if we are on a taxonomy archive page
                        // but the filter has cleared or changed that taxonomy's selected terms
                        const targetPathname = getTargetPathname(location.pathname, this.url, data.query)

                        // On taxonomy archive pages, strip the query parameter that is already
                        // encoded in the URL path to avoid duplicate URLs.
                        // e.g. /druh-nemovitosti/komercni-nemovitosti/?nemovitost=komercni-nemovitosti
                        // → should become: /druh-nemovitosti/komercni-nemovitosti/?lokalita=...
                        const taxParamMap: Record<string, string> = {
                            'druh-nemovitosti': 'nemovitost',
                            'typ-nabidky': 'typ_prodeje',
                            'okres': 'lokalita',
                        }
                        const pathParts = targetPathname.split('/').filter(Boolean)
                        for (const [segment, paramKey] of Object.entries(taxParamMap)) {
                            const taxIdx = pathParts.indexOf(segment)
                            if (taxIdx !== -1 && pathParts[taxIdx + 1]) {
                                const taxTermSlug = pathParts[taxIdx + 1]
                                const paramVal = historyQuery.get(paramKey)
                                if (paramVal) {
                                    const values = paramVal.split(',').map(v => v.trim())
                                    // Only strip if the URL param exactly matches the taxonomy term slug
                                    // (i.e., no additional types selected alongside it)
                                    if (values.length === 1 && values[0] === taxTermSlug) {
                                        historyQuery.delete(paramKey)
                                    }
                                }
                                break
                            }
                        }

                        const cleanQueryString = historyQuery.toString()
                        const cleanUrl = `${targetPathname}${cleanQueryString ? '?' + decodeURIComponent(cleanQueryString) : ''}`
                        history.pushState({}, "", cleanUrl)
                    }
                })
                .catch((e) => {
                    if (e.name !== "AbortError") console.error("Archive fetch error:", e)
                })
                .finally(() => {
                    this.isLoading = this.appendPosts = this.scrollIntoView = false
                })
        },

        async getSummary() {
            const queryString = this.prepareQueryString(this.selectedParams, "summary")
            const targetUrl = `${this.apiUrl}${queryString}`

            console.log("Fetching summary:", targetUrl)

            await fetch(targetUrl)
                .then((response) => response.json())
                .then((data) => {
                    this.filteredCount = data.count
                    if (data.filterParams) {
                        this.filterParams = data.filterParams
                    }
                })
        },

        get ui() {
            const getLabel = (key: string, def: string) => {
                const val = this.selectedParams[key]
                if (Array.isArray(val) && val.length) {
                    if (val.length === 1) {
                        // Find label in options
                        const opts = this.filterParams?.[key]?.options ?? {}
                        return opts[val[0]] || val[0]
                    }
                    return `${val.length} vybráno`
                }
                if (typeof val === 'string' && val) {
                    const opts = this.filterParams?.[key]?.options ?? {}
                    return opts[val] || val
                }
                return def
            }

            // Special handling for locality (address or radius)
            let locLabel = this.selectedParams['adresa_text'] || 'Celá nabídka'
            if (this.selectedParams['radius']) {
                const rOpts = { '0.5': '0,5 km', '1': '1 km', '5': '5 km', '10': '10 km', '25': '25 km', '50': '50 km' } as any
                locLabel += ` (+${rOpts[this.selectedParams['radius']] || this.selectedParams['radius'] + ' km'})`
            } else {
                // If checking 'lokalita' param (districts/regions)
                locLabel = getLabel('lokalita', locLabel)
            }

            return {
                locality: locLabel,
                saleType: getLabel('typ_prodeje', 'Vše'),
                estate: getLabel('nemovitost', 'Vše'),
            }
        },

        get hasLocality(): boolean {
            const loc = this.selectedParams['lokalita']
            const hasPredefined = Array.isArray(loc) && loc.length > 0
            const hasCustom = !!this.selectedParams['adresa_text']
            return hasPredefined || hasCustom
        },

        clearLocality() {
            this.selectedParams['lokalita'] = []
            this.selectedParams['adresa_text'] = ''
            this.selectedParams['lat'] = ''
            this.selectedParams['lng'] = ''
            this.selectedParams['radius'] = ''
            this.filterOffers()
        },

        handleLocalityCheckboxChange() {
            this.selectedParams['adresa_text'] = ''
            this.selectedParams['lat'] = ''
            this.selectedParams['lng'] = ''
            this.selectedParams['radius'] = ''

            if (this.activeDropdown === 'lokalita') {
                this.filterOffers()
            } else {
                this.getSummary()
            }
        },

        clearKey(key: string) {
            this.selectedParams[key] = []
            this.filterOffers()
        },
        clearPrice(key: string) {
            this.selectedParams[`${key}_od`] = ''
            this.selectedParams[`${key}_do`] = ''
            this.filterOffers()
        },
        toggleParam(key: string, value: string) {
            const current = this.selectedParams[key]
            if (Array.isArray(current)) {
                if (current.includes(value)) {
                    this.selectedParams[key] = current.filter(v => v !== value)
                } else {
                    this.selectedParams[key] = [...current, value]
                }
            } else {
                this.selectedParams[key] = [value]
            }
        },

        handleDialogLocalityChange() {
            // Inside dialog: clear custom address, only update preview count (no filterOffers)
            this.selectedParams['adresa_text'] = ''
            this.selectedParams['lat'] = ''
            this.selectedParams['lng'] = ''
            this.selectedParams['radius'] = ''
            this.getSummary()
        },

        get dialogActiveCount(): number {
            let count = 0
            const p = this.selectedParams as any

            // Check dispozice
            if (Array.isArray(p['dispozice']) && p['dispozice'].length > 0) count++

            // Check podtyp
            if (Array.isArray(p['podtyp']) && p['podtyp'].length > 0) count++

            // Check price
            if (p['cena_od'] || p['cena_do']) count++

            // Check locality districts
            if (Array.isArray(p['lokalita']) && p['lokalita'].length > 0) count++

            // Check custom address
            if (p['adresa_text']) count++

            // Check nemovitost (estate type)
            if (Array.isArray(p['nemovitost']) && p['nemovitost'].length > 0) count++

            // Check typ_prodeje (sale type)
            if (Array.isArray(p['typ_prodeje']) && p['typ_prodeje'].length > 0) count++

            return count
        },

        getFilterLink(key: string, value: string): string {
            const params = JSON.parse(JSON.stringify(this.selectedParams))

            if (Array.isArray(params[key])) {
                const index = params[key].indexOf(value)
                if (index > -1) {
                    params[key].splice(index, 1)
                } else {
                    params[key].push(value)
                }
            } else {
                if (params[key] === value) {
                    params[key] = ""
                } else {
                    params[key] = value
                }
            }

            const query = new URLSearchParams()
            this.queryOrder.forEach((param: string) => {
                let val = params[param] ?? ""
                if (val !== undefined && val !== null && val !== "") {
                    if (Array.isArray(val) && val.length === 0) return
                    if (param === 'adresa_text' && typeof val === 'string' && val.trim() !== '') {
                        val = slugify(val);
                    }
                    query.append(
                        param,
                        Array.isArray(val) ? val.sort().join(",") : String(val)
                    )
                }
            })

            query.get("strana") === "1" && query.delete("strana")
            query.get("razeni") === "nejnovejsi" && query.delete("razeni")
            query.delete("_t")
            query.delete("return")

            ;[...query.entries()].forEach(([k, v]) => {
                if (v === "" || v === "default" || v === "12" && k === "pocet") {
                    query.delete(k)
                }
            })

            const queryString = [...query].length ? `?${decodeURIComponent(query.toString())}` : ""
            return `${location.pathname}${queryString}`
        },

        // --- Address Autocomplete Logic ---
        addressSuggestions: [],
        addressDebounceTimer: null,

        handleAddressInput(event: Event) {
            const value = (event.target as HTMLInputElement).value
            this.selectedParams['lat'] = ''
            this.selectedParams['lng'] = ''

            if (this.addressDebounceTimer) clearTimeout(this.addressDebounceTimer)

            if (value.length < 3) {
                this.addressSuggestions = []
                return
            }

            this.addressDebounceTimer = setTimeout(() => {
                fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&countrycodes=cz&addressdetails=1&limit=10&viewbox=17.0,50.5,19.0,49.0`)
                    .then(res => res.json())
                    .then(data => {
                        const uniqueSuggestions: any[] = []
                        const seenLabels = new Set<string>()

                        if (Array.isArray(data)) {
                            data.forEach((item: any) => {
                                const formatted = getFormattedAddress(item)
                                if (!seenLabels.has(formatted.label)) {
                                    seenLabels.add(formatted.label)
                                    uniqueSuggestions.push({
                                        ...item,
                                        formattedLabel: formatted.label,
                                        addressType: formatted.type
                                    })
                                }
                            })
                        }
                        this.addressSuggestions = uniqueSuggestions
                    })
            }, 300)
        },

        selectAddress(suggestion: any) {
            const formatted = getFormattedAddress(suggestion)
            this.selectedParams['adresa_text'] = formatted.label
            this.selectedParams['lat'] = suggestion.lat
            this.selectedParams['lng'] = suggestion.lon
            this.selectedParams['lokalita'] = []
            
            if (formatted.type === 'street') {
                this.selectedParams['radius'] = '1'
            } else if (formatted.type === 'city') {
                this.selectedParams['radius'] = '5'
            } else {
                this.selectedParams['radius'] = '5'
            }

            this.addressSuggestions = []
            this.getSummary()
        }
    })

    const pager = document.querySelector("[data-pager]")
    pager?.querySelector("nav")?.addEventListener("click", (e) => {
        const target = e.target as HTMLElement
        if (!target.matches("a")) return

        e.preventDefault()
        const pageUrl = new URLSearchParams(new URL(target.getAttribute("href")!).search)
        archive.changePage(parseInt(pageUrl.get("strana") ?? "1", 10))
    })

    createApp({ archive }).mount(archiveGrid as HTMLElement)

    // Initialize pill background after mount
    setTimeout(() => archive.updatePillBackground(), 100)

    // Update on window resize
    window.addEventListener('resize', () => archive.updatePillBackground())
}
